import axiosConfig from "./axiosConfig";
import { PeopleRole } from "../models/peopleCandidate";

// One fact the drafter was allowed to phrase, shown in the modal's "Data used to
// write this" inspector with an include / exclude checkbox. `included: false` means
// the operator unticked it, so it was NOT rendered into the model's prompt on this
// turn (the cheap, deterministic control). The full candidate set always comes back
// - excluded items stay in the list so they can be re-ticked - so the checkboxes
// bind directly to `included`.
export interface OutreachInputItem {
  id: string;
  category: string;
  label: string;
  value: string;
  included: boolean;
  // Only the site-opportunity + proximity facts are toggleable; the recipient +
  // company facts are standard (always included, rendered without a checkbox).
  toggleable: boolean;
  // Set on a saved turn's fact when we now hold it and that draft did not, or held it
  // differently (typically the lead's site was created after the draft was written).
  // It sits in its normal place in the list, ticked, so a rerun uses it - the adorner
  // is what marks it as not yet part of the draft on screen.
  isNew?: boolean;
}

// Draft (or re-draft) a cold-outreach email for a lead's contact. A first call omits
// `outreachMessageId` and starts a thread; a rerun passes it back with the operator's
// `excludedInputIds` (unticked facts) and / or free-text `feedback`, appending a new
// turn to the same thread. `apolloId` picks which enriched contact to write to (the
// first is used if omitted).
export interface OutreachDraftRequest {
  role: PeopleRole;
  apolloId?: string | null;
  outreachMessageId?: string | null;
  excludedInputIds?: string[];
  feedback?: string | null;
  // How much of the (real) nearby-operational-site data the email may use as social
  // proof: "area_distance" (default - distance + area, don't name it),
  // "area_distance_size", "named" (name + exact distance), or "vague". The BE renders
  // the proximity fact at this level; softer levels never expose the site name.
  proximityDisclosure?: string | null;
  // The Kinde user email, for the "drafted by" audit label (BE falls back to the
  // access-token identity, which is the opaque kp_ sub - never a human email).
  userEmail?: string | null;
}

// The 202 the kickoff returns. Drafting is a live CRM read plus a Bedrock generation
// (26-30s measured), which straddles the 29s API-Gateway ceiling, so the POST starts an
// operation and the modal polls it; the draft itself arrives as the operation's result.
export interface OutreachDraftKickoff {
  status: string;
  operationKey: string;
  siteId: string;
  // Set when an identical draft was already in flight and this call joined it rather
  // than paying for a second generation.
  deduped?: boolean;
}

export interface OutreachDraftResponse {
  outreachMessageId: string;
  turnIndex: number;
  subject: string;
  body: string;
  // The facts this draft used. On a saved turn, any that have appeared or moved since
  // are merged in at their canonical position carrying `isNew`.
  inputs: OutreachInputItem[];
  // Set on a saved turn whose facts have since moved (typically the lead's site was
  // created after the draft was written, so its size + generation now exist). A rerun
  // picks the new facts up; this is how the operator knows to run one. Always false on
  // a fresh draft. Optional so an older BE simply reads as "nothing has changed". Wider
  // than the per-item `isNew`: a fact we have LOST since the draft flips this but has
  // no row to render.
  factsChanged?: boolean;
  traceRunId: string;
  // The thread-summary call's own trace, when the draft had a logged thread to read.
  // Separate from `traceRunId`: the relationship facts come from a distinct model call,
  // and judging whether to trust them means seeing that call, not the draft it fed.
  threadSummaryTraceRunId?: string | null;
  modelId: string;
  inputTokens: number;
  outputTokens: number;
  // Where THIS turn stands relative to the sender's drafts folder. `Placed` says a
  // draft exists for the thread at all; `MatchesThisTurn` says it holds this exact
  // wording, which is what separates "already waiting to go out" from "the folder
  // holds the version before your rerun". Optional so an older backend degrades to
  // offering the placement, which is the safe direction.
  gmailDraftPlaced?: boolean;
  gmailDraftMatchesThisTurn?: boolean;
  // The draft in the folder no longer matches what we last wrote into it, i.e.
  // somebody has edited it by hand. Rerunning would overwrite those edits, so this
  // is what turns the "rerunning replaces it" line into a warning.
  gmailDraftEdited?: boolean;
  // Whether the three fields above came from an actual read of the folder. False
  // means Gmail could not be reached, so they carry our stored view instead and the
  // UI must not assert what is in the folder. Absent from an older backend, which
  // reads as unverified - the safe direction.
  gmailDraftStateVerified?: boolean;
  sentAt?: string | null;
  // What the automatic placement did with this turn. "placed" is the normal path;
  // "edited" and "vanished" are the two cases it deliberately declines because a
  // person has to decide (replacing hand edits, or re-placing a draft that may
  // already have been sent), and only those two put a button back on screen.
  gmailAutoPlace?:
    | "placed"
    | "no_email"
    | "not_connected"
    | "already_sent"
    | "edited"
    | "vanished"
    | "failed";
}

const postOutreachDraft = async (
  authToken: string,
  leadId: string,
  body: OutreachDraftRequest
): Promise<OutreachDraftKickoff> => {
  const { data } = await axiosConfig.post(
    `/leads/${leadId}/outreach/draft`,
    body,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export default postOutreachDraft;
